import React, { useState } from "react";
import { api, apiError } from "@/lib/apiClient";
import { Navigation, Loader2, MapPin, Clock, User } from "lucide-react";
import { toast } from "sonner";

export default function PlusProche() {
  const [cp, setCp] = useState("");
  const [results, setResults] = useState([]);
  const [point, setPoint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const search = async (e) => {
    e?.preventDefault();
    if (cp.length < 2) { toast.error("Saisissez au moins 2 chiffres de code postal"); return; }
    setLoading(true);
    setErrMsg("");
    try {
      const { data } = await api.get(`/techniciens/proches?code_postal=${cp}`);
      setResults(Array.isArray(data?.techniciens) ? data.techniciens : []);
      setPoint(data?.point || null);
      setSearched(true);
    } catch (err) { const m = apiError(err); toast.error(m); setErrMsg(m); setResults([]); setSearched(true); }
    finally { setLoading(false); }
  };

  const dispoColor = (d) => d === "Disponible" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700";

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Plus proche</h1>
        <p className="text-sm text-slate-500">Trouvez les techniciens géographiquement les plus proches d'un point de départ (distance réelle par géocodage).</p>
      </div>

      <form onSubmit={search} className="rounded-xl border border-slate-200 bg-white p-4">
        <label className="mb-2 block text-[11px] font-bold uppercase tracking-widest text-slate-400">Point de départ</label>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="pp-cp"
              value={cp}
              onChange={(e) => setCp(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
              placeholder="Code postal — ex : 69008"
              className="w-56 rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-slate-900"
            />
          </div>
          <button data-testid="pp-search" disabled={loading} className="flex items-center gap-2 rounded-lg bg-[#0A0A0A] px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />} Rechercher les techniciens
          </button>
          <span className="text-xs text-slate-400">Adresse / coordonnées GPS possibles ultérieurement.</span>
        </div>
      </form>

      {searched && (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid="pp-results">
          {(results || []).length === 0 ? <p className="p-6 text-sm text-slate-400">{errMsg || "Aucun technicien géolocalisé trouvé."}</p> : (
            <table className="w-full text-left text-sm">
              <thead><tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2.5">#</th><th className="px-4 py-2.5">Technicien</th><th className="px-4 py-2.5">Localisation</th><th className="px-4 py-2.5">Disponibilité</th><th className="px-4 py-2.5">Prochain RDV</th><th className="px-4 py-2.5 text-right">Distance</th>
              </tr></thead>
              <tbody>
                {(results || []).map((t, i) => (
                  <tr key={t.id} data-testid={`pp-tech-${t.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-3"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">{i + 1}</span></td>
                    <td className="px-4 py-3"><div className="flex items-center gap-2 font-medium text-slate-900"><User className="h-4 w-4 text-slate-400" />{t.nom}</div></td>
                    <td className="px-4 py-3 text-slate-600">{t.ville} <span className="mono text-xs text-slate-400">{t.code_postal}</span></td>
                    <td className="px-4 py-3"><span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${dispoColor(t.disponibilite)}`}>{t.disponibilite}</span></td>
                    <td className="px-4 py-3 text-xs text-slate-500">{t.prochain_rdv || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="mono text-sm font-bold text-blue-600">{t.distance_km != null ? `${t.distance_km} km` : "—"}</div>
                      {t.temps_trajet_min != null && <div className="flex items-center justify-end gap-0.5 text-[11px] text-slate-400"><Clock className="h-3 w-3" />~{t.temps_trajet_min} min</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
